import { useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { DateField, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { RcnUserContext } from 'src/context/RcnUser/RcnUserContext';
import { WildCardRouteContext } from 'src/context/WildCardRoute/WildCardRouteContext';

import useContentAccessLevel from 'src/hooks/useContentAccessLevel';

import { ComponentProps, SitecoreContextValue } from 'lib/ComponentProps';

import { parseAccessLevelContent } from 'src/mappers/fields/parseAccessLevelContent';

import IsSitecoreEditing from 'src/utils/isSitecoreEditing';
import { isClinicalTopic, isResourceContentPage, isResourcePage } from 'src/utils/isTemplate';

import { Button } from 'src/components/basicContent/_Button/Button';
import { Favourite } from 'src/components/search/_Favourite/_Favourite';
import { ShareButton } from 'src/components/search/_ShareButton/ShareButton';

import CalendarIconSvg from 'public/icons/calendar.svg';
import CloseIconSvg from 'public/icons/close.svg';
import EllipsisIconSvg from 'public/icons/Ellipsis.svg';

import { ContentOverviewSummaryFields } from '../contentOverviewSummary/ContentOverviewSummaryFields';

import DownloadButton from './_contentOverviewDownloadButton/DownloadButton';
import ReadTime from './_contentOverviewReadTime/ReadTime';

import { ContentOverviewToolbarFields } from './ContentOverviewToolbarFields';

import styles from './ContentOverviewToolbar.module.css';

type ContentOverviewToolbarProps = ComponentProps & {
	fields: ContentOverviewToolbarFields & ContentOverviewSummaryFields;
};

export default function ContentOverviewToolbar(props: ContentOverviewToolbarProps): JSX.Element {
	const router = useRouter();

	const { authenticatedUserProfile } = useContext(RcnUserContext);
	const { sitecoreContext } = useSitecoreContext<SitecoreContextValue>();
	const WildCardRoute = useContext(WildCardRouteContext);

	const access = parseAccessLevelContent(props.fields.AccessType);
	const { isAllowedAccessToContent } = useContentAccessLevel(access);

	const isEditing = IsSitecoreEditing();

	const container = useRef<HTMLDivElement>(null);
	const actionsItemsElements = useRef<HTMLDivElement>(null);
	const showOverflowButton = useRef<HTMLButtonElement>(null);
	const overflowWrapper = useRef<HTMLDivElement>(null);

	const [widths, setWidths] = useState<number[]>([]);

	const [overflowIndex, setOverflowIndex] = useState<number>(0);
	const [initialised, setInitialised] = useState(false);

	const [showOverFlowMenu, setShowOverFlowMenu] = useState<boolean>(false);

	const templateId =
		WildCardRoute.wildCardItemInfo?.TemplateId ?? props.fields?.WildCardContentTemplateId ?? sitecoreContext.route.templateId;
	const hideReadTime = ((!isResourcePage(templateId) && !isResourceContentPage(templateId)) || props.fields?.HideReadTime?.value) ?? true;
	const hideDownload = props.fields?.HidePdfDownload?.value || isClinicalTopic(templateId) || !isAllowedAccessToContent;

	const toolbarItems = (
		<>
			{/**
			 * Last Reviewed / Updated Date
			 */}
			{(props.fields?.DateOfLastQualityAssuranceReview || props.fields?.lastUpdatedDate) && (
				<div className={styles.toolBarItemContainer}>
					<div
						title={
							props.fields.DateOfLastQualityAssuranceReview
								? sitecoreContext.dictionary?.lastReviewedTooltip
								: sitecoreContext.dictionary?.lastUpdatedTooltip
						}
					>
						<div className={styles.lastReviewedWrapper}>
							<CalendarIconSvg className={styles.toolBarItemIcon} />

							{props.fields.DateOfLastQualityAssuranceReview ? (
								<>
									<Text field={{ value: sitecoreContext.dictionary?.lastReviewedText }} />
									<>&nbsp;</>
									{new Date(props.fields.DateOfLastQualityAssuranceReview.value) > new Date(2000, 0, 1) ? (
										<DateField
											field={props.fields.DateOfLastQualityAssuranceReview}
											render={(date: Date) => date && date.toLocaleDateString('en-GB')}
										/>
									) : (
										<Text field={{ value: sitecoreContext.dictionary.noDateAvailable }} />
									)}
								</>
							) : (
								<Text
									field={{
										value: `${sitecoreContext.dictionary?.lastUpdatedText ? sitecoreContext.dictionary?.lastUpdatedText : ''} ${
											props.fields.lastUpdatedDate
										}`,
									}}
								/>
							)}
						</div>
					</div>
				</div>
			)}

			{/**
			 * Read Time
			 */}
			{!hideReadTime && (
				<ReadTime
					fields={{
						currentResourceUrl: props.fields?.currentResourceUrl,
						allResourceUrls: props.fields?.allResourceUrls,
					}}
				/>
			)}

			{/**
			 * Course Duration
			 */}
			{props.fields?.CourseDuration?.value && (
				<div className={styles.toolBarItemContainer}>
					<div className={styles.courseDurationTooltipContainer} title={sitecoreContext.dictionary?.courseDurationTooltip}>
						<div className={styles.courseDurationContainer}>
							<CalendarIconSvg className={styles.courseDurationIcon} />
							<Text field={props.fields?.CourseDuration} />
						</div>
					</div>
				</div>
			)}

			{/**
			 * Share Content
			 */}
			<div className={styles.toolBarItemContainer}>
				<ShareButton
					shareButtonText={props.fields?.ShareButtonText}
					shareButtonToolTip={props.fields?.ShareButtonToolTip}
					shareOptions={props.fields?.ShareOptions}
					shareTitle={props.fields?.PageTitle?.value || props.fields?.Title?.value || `${sitecoreContext.route?.fields?.PageTitle?.value}`}
					shareUrl={props.fields?.currentResourceUrl ? props.fields?.currentResourceUrl : process.env.PUBLIC_URL + router.asPath}
					noShareOptions={props.fields?.NoShareOptions}
					alignLeft
					toolbarIconSize
				/>
			</div>
			{/**
			 * Favourite Content
			 */}
			{authenticatedUserProfile && (
				<div className={styles.toolBarItemContainer}>
					<Favourite
						itemId={sitecoreContext.wildcardMetaData?.pageID || sitecoreContext.itemId || ''}
						showText
						favouriteButtonText={props.fields?.FavouriteButtonText}
						favouriteButtonToolTip={props.fields?.FavouriteButtonToolTip}
						unfavouriteText={props.fields?.UnfavouriteText}
						toolbarIconSize
					/>
				</div>
			)}

			{/**
			 * Download Content
			 */}
			{!hideDownload && (
				<div className={styles.toolBarItemContainer}>
					<DownloadButton {...props} />
				</div>
			)}
		</>
	);

	const filteredToolbarItems = toolbarItems.props.children.filter((toolbarItem: HTMLElement | boolean | string) => {
		return toolbarItem !== undefined && toolbarItem !== false && toolbarItem !== '';
	});

	useEffect(() => {
		if (isEditing) {
			return;
		}

		const updateWidths = () => {
			const newWidths = [...(actionsItemsElements.current?.childNodes || [])].map((el: HTMLElement) => {
				return el?.offsetWidth;
			});

			// Update widths only if there's a change and all elements have width
			if (JSON.stringify(newWidths) !== JSON.stringify(widths) && newWidths.every((x) => x)) {
				setWidths(newWidths);
			}
		};

		updateWidths();

		setTimeout(() => {
			if (initialised) {
				calculateOverflowIndex();
			}
		}, 100);

		window.addEventListener('resize', calculateOverflowIndex);

		return () => window.removeEventListener('resize', calculateOverflowIndex);
	}, [widths, authenticatedUserProfile, initialised]);

	useEffect(() => {
		if (!initialised) {
			setInitialised(true);
			calculateOverflowIndex();
		}
	}, [initialised]);

	function calculateOverflowIndex() {
		if (!container.current) {
			return;
		}

		const itemGap = parseInt(globalThis.window.getComputedStyle(container.current, null).getPropertyValue('gap'));
		const targetIndex = widths.findIndex((_x, i) => {
			if (i === 0) {
				return false;
			}
			const totalWidth = widths.slice(0, i + 1).reduce((a, b) => a + b);
			return totalWidth + itemGap * (i + 1) + 60 >= (container.current?.offsetWidth || 0);
		});

		const safeIndex = targetIndex >= 0 ? Math.max(1, targetIndex) : widths.length;

		setOverflowIndex(safeIndex > 0 ? safeIndex : widths.length > 0 ? widths.length : filteredToolbarItems.length);
	}

	return (
		<div className={`${styles.toolBarContainer} maxWidth`}>
			<div className={styles.toolBarWrapper} ref={container}>
				<div className={styles.toolBarActionItems} ref={actionsItemsElements}>
					{filteredToolbarItems.slice(0, overflowIndex)}
				</div>
				{overflowIndex >= 0 && overflowIndex < filteredToolbarItems.length && (
					<div className={styles.toolBarOverFlowContainer}>
						<Button
							title="showOverflow"
							buttonRef={showOverflowButton}
							className={styles.showOverflowButton}
							onClick={() => setShowOverFlowMenu(!showOverFlowMenu)}
							onBlur={(e: React.FocusEvent<HTMLButtonElement, Element> | undefined) => {
								setShowOverFlowMenu(false);

								overflowWrapper.current?.childNodes.forEach((elem: HTMLElement) => {
									if (e?.relatedTarget === elem.childNodes[0].childNodes[0]) {
										setShowOverFlowMenu(true);
									}
								});
							}}
						>
							<span className={styles.showOverflowButtonIcon}>
								<EllipsisIconSvg />
							</span>
						</Button>
						<div className={`${styles.toolBarOverOverFlowModal} ${showOverFlowMenu ? styles.active : ''}`}>
							<Button title="Close" className={styles.closeModalButton} onClick={() => setShowOverFlowMenu(false)}>
								<CloseIconSvg />
							</Button>
							<div className={styles.toolBarOverOverFlowModalContainer} ref={overflowWrapper}>
								{filteredToolbarItems.slice(overflowIndex, filteredToolbarItems.length)}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}
